<script lang="ts">
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'vue-chartjs';
import { defineComponent, computed, PropType } from 'vue';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default defineComponent({
  name: 'CostRampupGraph',
  components: {
    LineChart: Line,
  },
  props: {
    currentCost: {
      type: Number as PropType<number>,
      required: true
    },
    minCost: {
      type: Number as PropType<number>,
      default: 250
    },
    maxCost: {
      type: Number as PropType<number>,
      default: 500
    },
    steps: {
      type: Number as PropType<number>,
      default: 25
    }
  },
  setup(props) {
    // Command hierarchy cost formula: max(0, pow(shipCount - 250, 2) * 0.004432)
    const calculateCommandHierarchyCost = (shipCount: number): number => {
      if (shipCount <= 250) return 0;
      const excessShips = shipCount - 250;
      return Math.floor(Math.pow(excessShips, 2) * 0.004432);
    };

    const chartData = computed(() => {
      const labels: string[] = [];
      const costData: number[] = [];
      const currentCostData: (number | null)[] = [];
      let maxCostValue = 0;
      
      // Generate data points from minCost to maxCost
      for (let shipCount = props.minCost; shipCount <= props.maxCost; shipCount += props.steps) {
        const cost = calculateCommandHierarchyCost(shipCount);
        labels.push(shipCount.toString());
        costData.push(cost);
        maxCostValue = Math.max(maxCostValue, cost);
        
        // Add current cost vertical line data
        if (shipCount === props.currentCost || (shipCount <= props.currentCost && shipCount + props.steps > props.currentCost)) {
          currentCostData.push(maxCostValue * 1.1); // Make line slightly taller than max
        } else {
          currentCostData.push(null);
        }
      }

      // Add exact current cost point if it doesn't align with steps
      const exactCurrentIndex = labels.findIndex(label => parseInt(label) >= props.currentCost);
      if (exactCurrentIndex !== -1 && parseInt(labels[exactCurrentIndex]) !== props.currentCost) {
        const currentCost = calculateCommandHierarchyCost(props.currentCost);
        labels.splice(exactCurrentIndex, 0, props.currentCost.toString());
        costData.splice(exactCurrentIndex, 0, currentCost);
        currentCostData.splice(exactCurrentIndex, 0, maxCostValue * 1.1);
      }

      return {
        labels,
        datasets: [
          {
            label: 'Cost',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: 'rgba(59, 130, 246, 0.8)',
            data: costData,
            fill: true,
            tension: 0.3,
            pointRadius: 0,
            pointBackgroundColor: 'rgba(59, 130, 246, 1)',
            order: 2
          },
          {
            label: `Current Cost (${props.currentCost})`,
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            borderColor: 'rgba(239, 68, 68, 0.8)',
            data: currentCostData,
            fill: false,
            tension: 0,
            pointRadius: 0,
            borderWidth: 3,
            borderDash: [5, 5],
            order: 1
          },
        ],
      };
    });

    const options = computed(() => {
      const maxCost = calculateCommandHierarchyCost(props.maxCost);
      
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: {
              display: true,
              text: 'Ship Count',
              color: '#475569'
            },
            ticks: {
              color: '#475569'
            },
            grid: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            max: Math.min(Math.max(maxCost * 1.2, 100), 100000),
            title: {
              display: true,
              text: 'Command Hierarchy Cost',
              color: '#475569'
            },
            ticks: {
              color: '#475569'
            },
            grid: {
              color: '#475569'
            }
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            filter: function(tooltipItem: any) {
              return tooltipItem.datasetIndex === 0;
            }
          },
        },
        animation: {
          duration: 0,
        }
      };
    });

    return {
      chartData,
      options,
    };
  },
});
</script>

<template>
  <div class="cost-rampup-graph">
    <LineChart :data="chartData" :options="options" />
  </div>
</template>

<style scoped lang="scss">
.cost-rampup-graph {
  width: 100%;
  height: 100%;
  max-width: 100%;
  overflow: hidden;
}
</style>